import { useEffect, useContext } from "react";
import { BotReplyLayout } from "./bot.reply.layout.component";
import { ActionStatusType, useNexthinkContext } from "./nexthink.context";
import { AiAssistantContext } from "../../../../context/AiAssistantContext";
import { Icon } from "@me8eon/icons";
import LoaderComponent from "../../../loader-component/LoaderComponent";
import { useAlert } from "@me8eon/alert";
import { fetchTriggerStatus } from "@me8eon/ai.tools.nextthink";
import { useJwtToken } from "@me8eon/react_authentication";

const POLLING_INTERVAL = 5000;

const actionMessages = {
  success: {
    message: "chatBot.nexthinkIntegration.smartSolution.selectedActionsCompleted",
    note: ""
  },
  failed: {
    message: "chatBot.nexthinkIntegration.smartSolution.selectedActionsFailed",
    note: ""
  },
  progress: {
    message: "chatBot.nexthinkIntegration.smartSolution.selectedActionsProgress",
    note: "chatBot.nexthinkIntegration.smartSolution.selectedActionsProgressNote",
  },
  partial: {
    message: "chatBot.nexthinkIntegration.smartSolution.selectedActionsPartiallyCompleted",
    note: ""
  },
} as const;

export const NexthinkActionStatus: React.FC = () => {
  const { actionStatus, setActionStatus, manualRecommendedActions, localT } = useNexthinkContext();
  const { addManualRecommendedActions } = useContext(AiAssistantContext);
  const { showAlert } = useAlert();
  const jwtTokenFn = useJwtToken();

  useEffect(() => {
    if (actionStatus?.status !== "progress" || !actionStatus.response) return;

    const taskStatusMap: Record<string, string> = {};

    const interval = setInterval(async () => {
      try {
        await Promise.all(
          actionStatus.response.map(async (task: { value: { id: string, action: string } }, index: number) => {
            const requestId = task.value.id;
            if(requestId.startsWith(task.value.action)) {
              taskStatusMap[index] = "failure";
            }
            if (taskStatusMap[requestId]) return;

            const { data } = await fetchTriggerStatus(requestId, jwtTokenFn);
            if (data.length) {
              const status = data[0]["remote_action.execution.status"];
              if (["success", "failure"].includes(status)) {
                taskStatusMap[requestId] = status;
              }
            }
          })
        );

        const completedCount = Object.keys(taskStatusMap).length;
        if (completedCount === actionStatus.response.length) {
          clearInterval(interval);

          const statuses = Object.values(taskStatusMap);
          const allSuccess = statuses.every((s) => s === "success");
          const allFailure = statuses.every((s) => s === "failure");

          let alertTitle =  '';
          let alertMessage = '';
          let alertVariant: "success" | "failure" = "success";
          let actionStatus: ActionStatusType = "success";
          if (allSuccess) {
            actionStatus = "success";
            alertTitle = localT("chatBot.nexthinkIntegration.smartSolution.alerts.successTitle");
            alertMessage = localT("chatBot.nexthinkIntegration.smartSolution.alerts.successMessage");
            alertVariant = "success";
          } else if (allFailure) {
            actionStatus = "failed";
            alertTitle = localT("chatBot.nexthinkIntegration.smartSolution.alerts.errorTitle");
            alertMessage = localT("chatBot.nexthinkIntegration.smartSolution.alerts.errorMessage");
            alertVariant = "failure";
          } else {
            actionStatus = "partial";
            alertTitle = localT("chatBot.nexthinkIntegration.smartSolution.alerts.partialTitle");
            alertMessage = localT("chatBot.nexthinkIntegration.smartSolution.alerts.partialMessage");
            alertVariant = "failure";
          }

          if(manualRecommendedActions.length > 0) {
            alertMessage = `${alertMessage} \n ${localT("chatBot.nexthinkIntegration.smartSolution.manualActionNotification")}`;
            addManualRecommendedActions(manualRecommendedActions);
          }
          setActionStatus({ status: actionStatus });
          showAlert({
            title: alertTitle,
            message: alertMessage,
            variant: alertVariant,
          });
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [actionStatus, setActionStatus, showAlert, localT, manualRecommendedActions, addManualRecommendedActions, jwtTokenFn]);

  if (!actionStatus?.status) return null;

  const { message, note } = actionMessages[actionStatus.status];

  return (
    <BotReplyLayout>
      <div className="nexthink-action-status">
        <div className="action-status">
          <span>
            {actionStatus.status === "progress" ? (
              <LoaderComponent isLoading isInline size="small" />
            ) : (
              <Icon
                size="small"
                name={actionStatus.status === "failed" ? "task_failed" : "tick_small"}
                {...(actionStatus.status !== "failed" && { themedColor: "#34C759" })}
              />
            )}
          </span>
          <div>
            <span>{localT(message)}</span>
          </div>
        </div>
        <div className="action-note">
          {note && (<span>{localT(note)}</span>)}
        </div>
      </div>
    </BotReplyLayout>
  );
};
